import { ListingType, syntaxFNMA } from '@aloft/toolkit-shared';
import { Flex, theme } from '@aloft/toolkit-sui';

import { useGetListing } from '../../controller/hooks/listings/useGetListing.ts';
import { getReferenceProperty } from '../../modules/utils/getReferenceProperty.ts';
import {
  selectCompSequenceByListingType,
  selectPropertyTitleById,
  selectRentalPropertyTitleById,
} from '../../redux/features/selectors/orderSelectors.ts';
import { selectPropertyIdentificationById } from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import {
  selectToolkitListingID,
  selectTransactionById,
} from '../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { getColorForStatusOrSubject } from '../../utils/getColorForStatusOrSubject.ts';
import { getPropertyPinStatus } from '../../utils/getPropertyPinStatus.ts';
import {
  PROPERTY_HEADER_IMAGE_HEIGHT,
  PROPERTY_HEADER_TOTAL_HEIGHT,
} from '../PropertyGrid/consts.ts';
import { PropertyGridType } from '../PropertyGrid/types.ts';
import { registerTkv2ReferenceProperty } from '../ReferenceBar/registerTkv2ReferenceProperty.ts';
import { ValuationTablePropertyHeaderAddress } from './internal/ValuationTablePropertyHeaderAddress.tsx';
import { ValuationTablePropertyHeaderColorBand } from './internal/ValuationTablePropertyHeaderColorBand.tsx';
import { ValuationTablePropertyHeaderImage } from './internal/ValuationTablePropertyHeaderImage.tsx';
import { ValuationTablePropertyHeaderPrice } from './internal/ValuationTablePropertyHeaderPrice.tsx';
import { ValuationTablePropertyHeaderTitle } from './internal/ValuationTablePropertyHeaderTitle.tsx';
import { usePropertyHeaderActions } from './internal/usePropertyHeaderActions.tsx';

type PropertyHeaderElementParams = {
  propertyId: string;
  gridType?: PropertyGridType;
  hideActions?: boolean;
  showDataSourceDrawerToggler?: boolean;
  listingType?: ListingType;
};

type PropertyHeaderContentProps = {
  propertyId: string;
  gridType?: PropertyGridType;
  hideActions: boolean;
  showDataSourceDrawerToggler: boolean;
  listingType?: ListingType;
  isSubject: boolean;
  propertyIdentification: NonNullable<ReturnType<typeof selectPropertyIdentificationById>>;
  title: ReturnType<typeof selectPropertyTitleById>;
  transaction: NonNullable<ReturnType<typeof selectTransactionById>>;
  listing: ReturnType<typeof useGetListing>;
  compSequence: ReturnType<typeof selectCompSequenceByListingType>;
};

const PropertyHeaderContent = ({
  propertyId,
  gridType,
  hideActions,
  showDataSourceDrawerToggler,
  listingType,
  isSubject,
  propertyIdentification,
  title,
  transaction,
  listing,
  compSequence,
}: PropertyHeaderContentProps) => {
  let propertyStatus:
    | syntaxFNMA.UadListingStatus
    | syntaxFNMA.RentalListingStatus
    | 'Off-Market'
    | 'Subject'
    | 'Subject (ARV)' = 'Subject';
  if (propertyId === 'subjectArv') {
    propertyStatus = 'Subject (ARV)';
  } else {
    propertyStatus =
      listingType === 'rentalListing'
        ? (transaction.rentalListingStatus ?? 'Off-Market')
        : (transaction.status ?? 'Off-Market');
  }

  const pinStatus = getPropertyPinStatus(propertyStatus);
  const pinColor = getColorForStatusOrSubject(propertyStatus);

  const referenceProperty = getReferenceProperty(propertyId, compSequence);
  const referenceBarProps = registerTkv2ReferenceProperty(referenceProperty);

  const { menu, controls, modals } = usePropertyHeaderActions({
    propertyId,
    showDataSourceDrawerToggler,
    title,
    gridType,
    listingType,
  });

  return (
    <Flex
      position="sticky"
      flexDirection="column"
      top="0"
      zIndex={theme.zIndices.sticky}
      backgroundColor="surface1"
      borderWidth="1px"
      boxShadow="0px 4px 8px 0px #0000000F"
      height={PROPERTY_HEADER_TOTAL_HEIGHT}
      minHeight={PROPERTY_HEADER_TOTAL_HEIGHT}
      maxHeight={PROPERTY_HEADER_TOTAL_HEIGHT}
      boxSizing="border-box"
      overflow="hidden"
      {...referenceBarProps}
    >
      <Flex
        height={PROPERTY_HEADER_IMAGE_HEIGHT}
        minHeight={PROPERTY_HEADER_IMAGE_HEIGHT}
        maxHeight={PROPERTY_HEADER_IMAGE_HEIGHT}
        overflow="hidden"
      >
        <ValuationTablePropertyHeaderImage propertyId={propertyId} hideActions={hideActions} />
        <Flex flexGrow={1} minWidth={0} height="100%" overflow="hidden" paddingY="4px">
          <Flex gap="4px" width="100%" height="100%" minWidth={0} overflow="hidden">
            <Flex
              flexDirection="column"
              flexGrow={1}
              minWidth={0}
              height="100%"
              overflow="hidden"
              paddingLeft="6px"
              gap="2px"
              justifyContent="center"
            >
              <ValuationTablePropertyHeaderTitle
                hideActions={hideActions}
                pinColor={pinColor}
                pinStatus={pinStatus}
                propertyId={propertyId}
                title={title}
              />
              <ValuationTablePropertyHeaderAddress address={propertyIdentification.address} />
              <ValuationTablePropertyHeaderPrice
                propertyId={propertyId}
                hideActions={hideActions}
                isSubject={isSubject}
                listing={listing}
                transaction={transaction}
                isFeatureGrid={showDataSourceDrawerToggler}
                listingType={listingType}
              />
            </Flex>
            {!hideActions && (
              <Flex
                flexDirection="column"
                flexShrink={0}
                height="100%"
                width="24px"
                alignItems="flex-end"
              >
                <Flex
                  height="24px"
                  minHeight="24px"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  {menu}
                </Flex>
                <Flex flexGrow={1} />
                <Flex flexDirection="column" alignItems="flex-end" flexShrink={0}>
                  {controls}
                </Flex>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
      <ValuationTablePropertyHeaderColorBand color={pinColor} />
      {!hideActions && modals}
    </Flex>
  );
};

export const PropertyHeader = ({
  propertyId,
  gridType,
  hideActions = false,
  showDataSourceDrawerToggler = false,
  listingType,
}: PropertyHeaderElementParams) => {
  const isSubject = ['subject', 'subjectArv'].includes(propertyId);
  const propertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, propertyId),
  );

  const title = useAppSelector((state) =>
    listingType === 'rentalListing'
      ? selectRentalPropertyTitleById(state, propertyId)
      : selectPropertyTitleById(state, propertyId),
  );
  const transaction = useAppSelector((state) => selectTransactionById(state, propertyId));
  const toolkitListingID = useAppSelector((state) => selectToolkitListingID(state, propertyId));
  const listing = useGetListing(toolkitListingID);
  const compSequence = useAppSelector((state) =>
    selectCompSequenceByListingType(state, listingType),
  );

  if (!propertyIdentification || !transaction) return null;

  return (
    <PropertyHeaderContent
      propertyId={propertyId}
      gridType={gridType}
      hideActions={hideActions}
      showDataSourceDrawerToggler={showDataSourceDrawerToggler}
      listingType={listingType}
      isSubject={isSubject}
      propertyIdentification={propertyIdentification}
      title={title}
      transaction={transaction}
      listing={listing}
      compSequence={compSequence}
    />
  );
};
